import {BeaconTrigger, GpsTrigger, Spot, TriggerRule, WiFiApTrigger, Zone} from "../../components/types";
import {buildSpotRuleGraph, isPositiveSpotEvent} from "../areaDetails/customFlow/spotRuleGraph";

export const UNGROUPED_CUE_GROUP_ID = "ungrouped-floating";
export const UNGROUPED_CUE_GROUP_NAME = "Ungrouped/Floating";

export type DerivedCueGroupTrigger = {
    beaconTriggers?: BeaconTrigger[];
    wiFiTriggers?: WiFiApTrigger[];
    gpsTriggers?: GpsTrigger[];
};

export type DerivedCueGroup = {
    id: string;
    name: string;
    spotKeys: string[];
    ambientZoneIds: string[];
    rootSpotKey?: string;
    trigger?: DerivedCueGroupTrigger;
    isFloating: boolean;
};

type ComponentDescriptor = {
    nodeIds: string[];
    edgeCount: number;
};

const cloneTriggerSource = (spot?: Spot): DerivedCueGroupTrigger => ({
    beaconTriggers: spot?.beaconTriggers ? [...spot.beaconTriggers] : undefined,
    wiFiTriggers: spot?.wiFiTriggers ? [...spot.wiFiTriggers] : undefined,
    gpsTriggers: spot?.gpsTriggers ? [...spot.gpsTriggers] : undefined
});

const getNodeOrderMap = (spotKeys: string[]) => {
    const order = new Map<string, number>();
    spotKeys.forEach((spotKey, index) => order.set(spotKey, index));
    return order;
};

const sortByNodeOrder = (a: string, b: string, order: Map<string, number>) => {
    return (order.get(a) ?? Number.MAX_SAFE_INTEGER) - (order.get(b) ?? Number.MAX_SAFE_INTEGER);
};

const describeComponents = (
    components: string[][],
    edges: Array<{fromId: string; toId: string}>
): ComponentDescriptor[] => {
    return components.map((nodeIds) => {
        const nodeSet = new Set(nodeIds);
        const edgeCount = edges.filter((edge) => nodeSet.has(edge.fromId) && nodeSet.has(edge.toId)).length;
        return {nodeIds, edgeCount};
    });
};

const orderSpotsInsideComponent = (
    nodeIds: string[],
    edges: Array<{fromId: string; toId: string}>,
    order: Map<string, number>
) => {
    const nodeSet = new Set(nodeIds);
    const componentEdges = edges.filter((edge) => nodeSet.has(edge.fromId) && nodeSet.has(edge.toId));
    const inDegree = new Map<string, number>();
    const nextByNode = new Map<string, string[]>();

    nodeIds.forEach((nodeId) => {
        inDegree.set(nodeId, 0);
        nextByNode.set(nodeId, []);
    });

    componentEdges.forEach((edge) => {
        inDegree.set(edge.toId, (inDegree.get(edge.toId) || 0) + 1);
        const next = nextByNode.get(edge.fromId) || [];
        next.push(edge.toId);
        nextByNode.set(edge.fromId, next);
    });

    const queue = nodeIds
        .filter((nodeId) => (inDegree.get(nodeId) || 0) === 0)
        .sort((left, right) => sortByNodeOrder(left, right, order));
    const ordered: string[] = [];

    while (queue.length > 0) {
        const nodeId = queue.shift();
        if (!nodeId) {
            continue;
        }
        ordered.push(nodeId);
        const nextNodes = [...(nextByNode.get(nodeId) || [])]
            .sort((left, right) => sortByNodeOrder(left, right, order));
        nextNodes.forEach((nextId) => {
            const degree = (inDegree.get(nextId) || 0) - 1;
            inDegree.set(nextId, degree);
            if (degree === 0) {
                queue.push(nextId);
                queue.sort((left, right) => sortByNodeOrder(left, right, order));
            }
        });
    }

    if (ordered.length < nodeIds.length) {
        const remaining = nodeIds
            .filter((nodeId) => !ordered.includes(nodeId))
            .sort((left, right) => sortByNodeOrder(left, right, order));
        ordered.push(...remaining);
    }

    return ordered;
};

const getRootSpotKey = (
    orderedSpotKeys: string[],
    incomingByNodeId: Map<string, Array<{fromId: string}>>
) => {
    return orderedSpotKeys.find((spotKey) => (incomingByNodeId.get(spotKey) || []).length === 0) || orderedSpotKeys[0];
};

const getAmbientStartSpotKey = (zone: Zone): string | undefined => {
    const rules = zone.triggerRules?.rules || [];
    const positiveSpotRule = rules.find((rule: TriggerRule) => {
        return rule.type === "Spot" && !!rule.spotKey && isPositiveSpotEvent(rule.eventType);
    });
    if (positiveSpotRule?.spotKey) {
        return positiveSpotRule.spotKey;
    }
    const beforeSpotRule = rules.find((rule: TriggerRule) => {
        return rule.type === "Spot"
            && !!rule.spotKey
            && (rule.eventType === "SPOT_NOT_TRIGGERED" || rule.eventType === "SPOT_NOT_FINISHED");
    });
    return beforeSpotRule?.spotKey;
};

const getFloatingSpotRelationKey = (spot?: Spot): string | undefined => {
    if (!spot) {
        return undefined;
    }
    const rules = spot.triggerRules?.rules || [];
    const negativeSpotRule = rules.find((rule: TriggerRule) => {
        return rule.type === "Spot"
            && !!rule.spotKey
            && (rule.eventType === "SPOT_NOT_TRIGGERED" || rule.eventType === "SPOT_NOT_FINISHED");
    });
    return negativeSpotRule?.spotKey;
};

/** For a spot with AMBIENT_TRIGGERED, return the referenced zone ID. */
const getAmbientTriggerZoneId = (spot?: Spot): string | undefined => {
    if (!spot) {
        return undefined;
    }
    const rules = spot.triggerRules?.rules || [];
    const ambientRule = rules.find((rule: TriggerRule) => {
        return rule.type === "Ambient"
            && !!rule.zoneKey
            && (rule.eventType === "AMBIENT_TRIGGERED" || rule.eventType === "AMBIENT_TIME_SINCE_TRIGGERED");
    });
    return ambientRule?.zoneKey;
};

/**
 * Derive groups by connected components in the positive rule graph.
 * Includes explicit handling of floating/unconnected cues and ambient zones.
 */
export const deriveCueGroups = (spots: Spot[], ambientZones: Zone[] = []): DerivedCueGroup[] => {
    const graph = buildSpotRuleGraph(spots);
    const orderedSpotKeys = Array.from(graph.spotByNodeId.keys());
    const nodeOrder = getNodeOrderMap(orderedSpotKeys);
    const components = describeComponents(graph.weakComponents, graph.edges);
    const groupBySpotKey = new Map<string, DerivedCueGroup>();
    const floatingSpotKeys: string[] = [];
    const groups: DerivedCueGroup[] = [];

    components.forEach((component) => {
        const isFloatingComponent = component.nodeIds.length === 1 && component.edgeCount === 0;
        if (isFloatingComponent) {
            floatingSpotKeys.push(component.nodeIds[0]);
            return;
        }

        const orderedInComponent = orderSpotsInsideComponent(component.nodeIds, graph.edges, nodeOrder);
        const rootSpotKey = getRootSpotKey(orderedInComponent, graph.incomingByNodeId);
        const rootSpot = rootSpotKey ? graph.spotByNodeId.get(rootSpotKey) : undefined;
        const nextGroup: DerivedCueGroup = {
            id: `derived-group-${groups.length + 1}`,
            name: rootSpot?.name || `Group ${groups.length + 1}`,
            spotKeys: orderedInComponent,
            ambientZoneIds: [],
            rootSpotKey,
            trigger: cloneTriggerSource(rootSpot),
            isFloating: false
        };
        groups.push(nextGroup);
        orderedInComponent.forEach((spotKey) => groupBySpotKey.set(spotKey, nextGroup));
    });

    // NOT-only spots can still trigger immediately (top placement),
    // but they remain related to the referenced spot's group.
    const unresolvedFloatingSpotKeys: string[] = [];
    floatingSpotKeys.forEach((spotKey) => {
        const spot = graph.spotByNodeId.get(spotKey);
        const relationSpotKey = getFloatingSpotRelationKey(spot);
        const relatedGroup = relationSpotKey ? groupBySpotKey.get(relationSpotKey) : undefined;
        if (!relatedGroup) {
            unresolvedFloatingSpotKeys.push(spotKey);
            return;
        }
        if (!relatedGroup.spotKeys.includes(spotKey)) {
            relatedGroup.spotKeys.push(spotKey);
        }
        groupBySpotKey.set(spotKey, relatedGroup);
    });

    // Link floating spots that reference ambient zones via AMBIENT_TRIGGERED
    // into the same group as the ambient's anchor group (or create a new group).
    const ambientZoneById = new Map(ambientZones.map((z) => [z.id, z]));
    const ambientGroupByZoneId = new Map<string, DerivedCueGroup>();
    const stillUnresolved: string[] = [];

    unresolvedFloatingSpotKeys.forEach((spotKey) => {
        const spot = graph.spotByNodeId.get(spotKey);
        const zoneId = getAmbientTriggerZoneId(spot);
        if (!zoneId) {
            stillUnresolved.push(spotKey);
            return;
        }
        // Check if this ambient's SPOT_FINISHED anchor puts it in an existing group
        const zone = ambientZoneById.get(zoneId);
        const anchorSpotKey = zone ? getAmbientStartSpotKey(zone) : undefined;
        const anchorGroup = anchorSpotKey ? groupBySpotKey.get(anchorSpotKey) : undefined;
        if (anchorGroup) {
            if (!anchorGroup.spotKeys.includes(spotKey)) {
                anchorGroup.spotKeys.push(spotKey);
            }
            if (!anchorGroup.ambientZoneIds.includes(zoneId)) {
                anchorGroup.ambientZoneIds.push(zoneId);
            }
            groupBySpotKey.set(spotKey, anchorGroup);
            ambientGroupByZoneId.set(zoneId, anchorGroup);
            return;
        }
        // No anchor group — create or join an ambient-based group
        let ambientGroup = ambientGroupByZoneId.get(zoneId);
        if (!ambientGroup) {
            ambientGroup = {
                id: `derived-group-${groups.length + 1}`,
                name: zone?.label || zone?.name || `Group ${groups.length + 1}`,
                spotKeys: [],
                ambientZoneIds: [zoneId],
                isFloating: false
            };
            groups.push(ambientGroup);
            ambientGroupByZoneId.set(zoneId, ambientGroup);
        }
        if (!ambientGroup.spotKeys.includes(spotKey)) {
            ambientGroup.spotKeys.push(spotKey);
        }
        groupBySpotKey.set(spotKey, ambientGroup);
    });

    const floatingSpotKeySet = new Set(stillUnresolved);
    const floatingAmbientZoneIds: string[] = [];
    ambientZones.forEach((zone) => {
        if (ambientGroupByZoneId.has(zone.id)) {
            return;
        }
        const startSpotKey = getAmbientStartSpotKey(zone);
        if (!startSpotKey) {
            floatingAmbientZoneIds.push(zone.id);
            return;
        }
        if (floatingSpotKeySet.has(startSpotKey)) {
            // The anchor spot is floating — create a group for both
            let group = groupBySpotKey.get(startSpotKey);
            if (!group) {
                const spot = graph.spotByNodeId.get(startSpotKey);
                group = {
                    id: `derived-group-${groups.length + 1}`,
                    name: spot?.name || `Group ${groups.length + 1}`,
                    spotKeys: [startSpotKey],
                    ambientZoneIds: [],
                    isFloating: false
                };
                groups.push(group);
                groupBySpotKey.set(startSpotKey, group);
                floatingSpotKeySet.delete(startSpotKey);
            }
            group.ambientZoneIds.push(zone.id);
            return;
        }
        const group = groupBySpotKey.get(startSpotKey);
        if (!group) {
            floatingAmbientZoneIds.push(zone.id);
            return;
        }
        group.ambientZoneIds.push(zone.id);
    });

    const hasFloatingGroup = unresolvedFloatingSpotKeys.length > 0 || floatingAmbientZoneIds.length > 0;
    if (hasFloatingGroup) {
        const orderedFloatingSpotKeys = [...unresolvedFloatingSpotKeys]
            .sort((left, right) => sortByNodeOrder(left, right, nodeOrder));
        groups.push({
            id: UNGROUPED_CUE_GROUP_ID,
            name: UNGROUPED_CUE_GROUP_NAME,
            spotKeys: orderedFloatingSpotKeys,
            ambientZoneIds: floatingAmbientZoneIds,
            isFloating: true
        });
    }

    return groups;
};
